export const common = {
  // Navbar
  nav: {
    home: 'Home',
    why: 'Why',
    applications: 'Applications',
    products: 'Products',
    resources: 'Resources',
    contacts: 'Contacts',
    askAI: 'Ask AI',
    myths: 'Myths vs Facts',
    o3101: 'O₃ 101',
    safety: 'Safety',
    faq: 'FAQ',
    blog: 'Blog',
  },
  
  // Hero
  hero: {
    badge: 'Educational Hub • Safety-first • Eco-tech',
    title: 'OzoneFusion 2.0',
    subtitle: 'Understanding ozone. Using it safely.',
    description: 'The first educational hub dedicated to ozone: clear guides, safety protocols, complete FAQs and an AI assistant always available.',
    cta1: 'Discover O₃ 101',
    cta2: 'Safety Guide',
    trust: {
      certified: 'Certified',
      certifiedDesc: 'EU Standard',
      scientific: 'Scientific',
      scientificDesc: 'Research based',
      aiSupport: 'AI Support',
      aiSupportDesc: '24/7 available',
      educational: 'Educational Hub',
      safetyFirst: 'Safety-first',
      ecoTech: 'Eco-tech',
    },
    scroll: 'Scroll to explore',
  },
  
  // Why Section
  why: {
    badge: 'Why OzoneFusion 2.0',
    title: 'An educational hub for everyone',
    subtitle: 'Clear information, guaranteed safety, sustainability at the core',
    description: 'We believe that knowledge is the first step towards a safe and conscious use of ozone. That is why we created this space.',
    cta: 'Start from O₃ 101',
    clarity: {
      title: 'Clarity',
      description: 'Scientific information accessible to everyone. No unnecessary jargon, only verified facts and clear explanations.',
    },
    safety: {
      title: 'Safety',
      description: 'Tested protocols and certified guidelines. Your safety is our absolute priority.',
    },
    sustainability: {
      title: 'Sustainability',
      description: 'Eco-friendly technology that respects the environment. Natural ozone, zero chemical residues.',
    },
  },
  
  // Myths vs Facts
  myths: {
    badge: 'Let\'s clarify',
    title: 'Myths vs Facts',
    subtitle: 'Debunking common misconceptions and discovering the scientific truth about ozone.',
    mythLabel: 'Myth',
    factLabel: 'Fact',
    footerLead: 'Want to learn more?',
    footerCta: 'Safety Guide',
    myth1: 'Ozone is always dangerous',
    fact1: 'It depends on dose, time, ventilation and correct procedures',
    myth2: 'It\'s a magic solution',
    fact2: 'It\'s chemistry: powerful if managed correctly with protocols',
    myth3: 'No need to ventilate after use',
    fact3: 'Ventilation is fundamental: minimum 30-60 minutes',
    myth4: 'Can replace traditional cleaning',
    fact4: 'It\'s a complement: first clean, then sanitize with O₃',
  },
  
  // Safety
  safety: {
    badge: 'Safety First',
    title: 'The 6 Golden Rules',
    subtitle: 'Follow these fundamental protocols for a safe and effective use of ozone.',
    checklistLead: 'Download the',
    checklistBold: 'Complete Safety Checklist',
    checklistTail: 'in PDF format',
    cta: 'Go to Resources',
    rule1: {
      title: 'Ventilation',
      description: 'Always air out spaces for 30-60 minutes after each treatment. Open windows and doors.',
    },
    rule2: {
      title: 'Correct Timing',
      description: 'Follow recommended treatment times. Never exceed the durations indicated by the manufacturer.',
    },
    rule3: {
      title: 'Safe Concentrations',
      description: 'Use appropriate concentrations for each application. More is not always better.',
    },
    rule4: {
      title: 'No Presence',
      description: 'Children, pets and people must leave the environment during the treatment.',
    },
    rule5: {
      title: 'Compatible Materials',
      description: 'Check compatibility with natural rubber, some metals and sensitive materials.',
    },
    rule6: {
      title: 'Maintenance',
      description: 'Regularly inspect the device. Replace filters and components according to the instructions.',
    },
  },
  
  // Applications
  applications: {
    badge: 'Where to use it',
    title: 'Ozone Applications',
    subtitle: 'From home to business: discover how ozone can improve hygiene and safety.',
    tabHome: 'Home',
    tabB2B: 'B2B',
    footerLead: 'Not sure which application is right for you?',
    footerCta: 'Ask the AI Coach',
    discoverProducts: 'Discover Products',
    homeItems: {
      item1: {
        title: 'Domestic Environments',
        description: 'Sanitize rooms, closets, bathrooms. Eliminate persistent odors and bacteria.',
      },
      item2: {
        title: 'Fabrics and Clothes',
        description: 'Sanitize clothes, curtains, sofas without chemicals.',
      },
      item3: {
        title: 'Fridge and Kitchen',
        description: 'Keep food fresh, eliminate mold and bad odors.',
      },
      item4: {
        title: 'Cars and Vehicles',
        description: 'Sanitize cabins, eliminate smoke, pollen and allergens.',
      },
    },
    b2bItems: {
      item1: {
        title: 'Hotel & Hospitality',
        description: 'Rapid sanitization of rooms, lobbies, spas. Certified protocols for safe guests.',
      },
      item2: {
        title: 'Professional Laundries',
        description: 'Sanitize linens, eliminate tough stains and odors. Savings on detergents.',
      },
      item3: {
        title: 'Offices and Commercial Spaces',
        description: 'Healthy work environments, reduced absenteeism, clean air.',
      },
      item4: {
        title: 'Healthcare Facilities',
        description: 'Deep sanitization of clinics, waiting rooms, instruments. Hospital standards.',
      },
    },
  },
  
  // Products
  products: {
    title: 'Our Products',
    subtitle: 'Italian technology for ozone sanitization: innovative solutions for home and business',
    cta: 'Learn more',
    tags: {
      washing: 'Washing Machine',
      italy: 'Made in Italy',
      spray: 'Sanitizing Spray',
      portable: 'Portable',
      generator: 'Generator',
      purifier: 'Purifier',
      compact: 'Compact',
      fridge: 'Fridge',
      powerbank: 'Power Bank',
      wellness: 'Wellness',
      pro: 'Professional',
    },
    items: {
      eco: {
        title: 'Eco OzoneFusion',
        description: 'The washing machine system that transforms water into natural detergent. Eliminates 99.9% of bacteria without chemical detergents.',
        feat1: 'Reduces detergents by 90%',
        feat2: 'Energy savings up to 70%',
        feat3: '5-year warranty',
      },
      acqua: {
        title: 'AcquaFusion',
        description: 'Portable spray that transforms water into a powerful natural sanitizer. Perfect for surfaces, objects and environments.',
        feat1: 'Powerful natural sanitization',
        feat2: 'No chemicals',
        feat3: '2-year warranty',
      },
      air: {
        title: 'O3 Air Ozone Generator',
        description: 'Ozone generator for complete sanitization of environments. Includes bubbler for ozonated water.',
        feat1: 'Air and water sanitization',
        feat2: '1-60 minute timer',
        feat3: '5-year warranty',
      },
      mini: {
        title: 'MINI Air Purifier',
        description: 'Compact purifier with negative ions. Perfect for bedrooms, offices or cars up to 30 m².',
        feat1: 'Natural negative ions',
        feat2: 'Silent and discreet',
        feat3: '2-year warranty',
      },
      fresh: {
        title: 'FreshZero',
        description: 'Mini fridge purifier with built-in power bank. Eliminates odors and prolongs freshness.',
        feat1: 'Eliminates odors and bacteria',
        feat2: 'Compact and portable',
        feat3: '2-year warranty',
      },
      sauna: {
        title: 'OzonoSauna',
        description: 'Professional system for saunas and wellness centers. Regenerating treatments with therapeutic ozone.',
        feat1: 'Wellness treatments',
        feat2: 'Professional use',
        feat3: 'Certified and safe',
      },
    },
  },
  
  // Resources
  resources: {
    title: 'Free resources',
    subtitle: 'Download guides, checklists and educational materials for safe ozone use',
    checklist: {
      title: 'Safety Checklist',
      description: 'Complete list of checks to perform before, during and after each treatment.',
      cta: 'Download PDF',
    },
    guide: {
      title: 'Quick O₃ Guide',
      description: 'Practical manual with everything you need to know to start safely.',
      cta: 'Download PDF',
    },
    poster: {
      title: 'Informative Poster',
      description: 'A4 poster to print with the golden rules for safe ozone use.',
      cta: 'Download PDF',
    },
  },
  
  // Blog
  blog: {
    title: 'Education & Safety',
    subtitle: 'Articles, guides and insights about ozone',
    comingSoon: 'Content coming soon',
    comingSoonDesc: 'We\'re preparing in-depth articles and guides for you. Check back soon!',
    cta: 'Subscribe to newsletter',
  },
  
  // AI Assistant
  ai: {
    badge: 'Advanced AI Assistant',
    title: 'Your O₃ Safety Coach',
    titleHighlight: 'Safety Coach',
    subtitle: 'An intelligent assistant always by your side to answer every question about ozone, safety of use, and best practices. Chat or voice, whenever you want.',
    features: {
      friendly: {
        title: 'Friendly & Clear',
        description: 'Simple and understandable answers, without unnecessary jargon',
      },
      responsible: {
        title: 'Responsible',
        description: 'Safety guardrails: no medical promises, only validated information',
      },
      knowledge: {
        title: 'Knowledge Base',
        description: 'Access to all guides, FAQs, and checklists on the site',
      },
      available: {
        title: 'Always Available',
        description: 'Chat and voice, 24/7, to answer your questions',
      },
    },
    ctaCard: {
      title: 'Ready to ask questions?',
      description: 'Click on the turquoise button at the bottom right to start a conversation. You can write or speak directly with the Coach.',
      cta: 'Open AI Coach',
    },
    examplesTitle: 'Or try one of these questions:',
    question1: 'How does ozone work?',
    question2: 'Is it safe for my home?',
    question3: 'How long should I ventilate?',
    question4: 'Can I use it with pets?',
    question5: 'Which concentrations are safe?',
    question6: 'How to sanitize fabrics?',
    safetyNotice: {
      title: 'Important Safety Note',
      description: 'The AI Coach provides educational information based on standards and scientific research. It does not offer medical or therapeutic advice. For clinical or health questions, always consult a qualified professional. The Coach will guide you to the right resources.',
    },
  },
  
  // About
  about: {
    title: 'About us',
    subtitle: 'Purevital / OzoneFusion',
    mission: 'Our mission',
    missionText: 'Making ozone technology accessible, safe and understandable for everyone. We believe in a cleaner future through education and responsible innovation.',
    values: 'Our values',
    value1: 'Scientific transparency',
    value2: 'Safety first',
    value3: 'Environmental sustainability',
    value4: 'Responsible innovation',
  },
  
  // Contact
  contact: {
    title: 'Contact us',
    subtitle: 'We\'re here to answer your questions',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    message: 'Message',
    send: 'Send message',
    whatsapp: 'Message us on WhatsApp',
    emailUs: 'Send us an email',
    newsletter: 'Subscribe to newsletter',
    newsletterDesc: 'Receive updates, guides and safety tips',
    subscribe: 'Subscribe',
    success: 'Message sent successfully!',
    error: 'Error sending. Please try again.',
  },
  
  // Footer
  footer: {
    tagline: 'Education, safety and technology for a cleaner future',
    explore: 'Explore',
    education: 'Education',
    company: 'Company',
    support: 'Support',
    legal: 'Legal',
    contactUs: 'Contact us',
    faq: 'FAQ',
    guides: 'Safety & Guidelines',
    community: 'Community',
    privacy: 'Privacy Policy',
    cookies: 'Cookie Policy',
    disclaimer: 'Disclaimer',
    terms: 'Terms and Conditions',
    allProducts: 'All Products',
    compareProducts: 'Compare Products',
    rights: 'All rights reserved',
    poweredBy: 'Powered by Readdy',
  },
  
  // Common
  common: {
    readMore: 'Read more',
    learnMore: 'Learn more',
    download: 'Download',
    close: 'Close',
    back: 'Back',
    next: 'Next',
    loading: 'Loading...',
  },
};
